// Settings.jsx — System configuration & health page
import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const REFRESH_MS = 30_000;

function dot(status) {
  if (status === "ok" || status === "running" || status === "present") return "bg-green-500";
  if (status === "not_configured" || status === "missing" || status === "disconnected") return "bg-yellow-400";
  return "bg-red-500";
}
function label(status, customOk) {
  if (status === "ok" || status === "running" || status === "present")
    return { text: customOk || (status === "running" ? "Running" : status === "present" ? "Fresh" : "OK"), color: "text-green-600" };
  if (status === "not_configured") return { text: "Not configured", color: "text-gray-400" };
  if (status === "missing")        return { text: "Missing", color: "text-yellow-600" };
  if (status === "disconnected")   return { text: "Disconnected", color: "text-yellow-600" };
  return { text: "Error", color: "text-red-600" };
}

function Row({ name, status, extra, customOk }) {
  const lbl = label(status ?? "error", customOk);
  return (
    <div className="flex items-center gap-3 text-xs py-1.5 border-b border-gray-100 last:border-0">
      <span className={`w-2 h-2 rounded-full inline-block flex-shrink-0 ${dot(status ?? "error")}`} />
      <span className="text-gray-700 flex-1">{name}</span>
      {extra && <span className="text-gray-400 font-mono">{extra}</span>}
      <span className={`${lbl.color} font-medium`}>{lbl.text}</span>
    </div>
  );
}

function SectionHead({ title }) {
  return <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">{title}</p>;
}

function InfoRow({ name, value, mono }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-0 text-xs">
      <span className="text-gray-500">{name}</span>
      <span className={`${mono ? "font-mono" : ""} text-gray-800`}>{value ?? "—"}</span>
    </div>
  );
}

export default function Settings() {
  const [sys,  setSys]  = useState(null);
  const [root, setRoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastOk, setLastOk]   = useState(null);
  const [activeTab, setActiveTab] = useState("health");

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/system-status`).then(r => r.json()).catch(() => null),
      fetch(`${API}/`).then(r => r.json()).catch(() => null),
    ]).then(([s, r]) => {
      setSys(s); setRoot(r);
      if (s) setLastOk(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); const id = setInterval(load, REFRESH_MS); return () => clearInterval(id); }, []);

  const ramPct   = sys?.ram?.pct ?? 0;
  const ramColor = ramPct > 85 ? "bg-red-500" : ramPct > 65 ? "bg-yellow-400" : "bg-green-500";
  const ramText  = ramPct > 85 ? "text-red-600" : ramPct > 65 ? "text-yellow-600" : "text-green-600";

  const TABS = [
    { id: "health",    label: "Health" },
    { id: "scheduler", label: "Scheduler" },
    { id: "score",     label: "Score System" },
    { id: "config",    label: "Config" },
  ];

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Settings</h2>
        <div className="flex items-center gap-3">
          {lastOk && <span className="text-gray-400 text-xs">{lastOk}</span>}
          <button onClick={load} disabled={loading}
            className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 transition-colors">
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 pb-0">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors border-b-2 -mb-px
              ${activeTab === t.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-400 hover:text-gray-600"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── HEALTH TAB ─────────────────────────────────────── */}
      {activeTab === "health" && (
        <div className="space-y-4">
          {!sys && !loading && <p className="text-red-600 text-xs">Backend unreachable</p>}

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Core Services" />
            {loading && !sys
              ? [1,2,3,4].map(i => (
                  <div key={i} className="flex gap-3 animate-pulse py-1.5 border-b border-gray-100">
                    <div className="w-2 h-2 rounded-full bg-gray-200 mt-0.5" />
                    <div className="h-3 bg-gray-200 rounded w-40" />
                    <div className="h-3 bg-gray-200 rounded w-16 ml-auto" />
                  </div>
                ))
              : sys && <>
                  <Row name="PostgreSQL"    status={sys.database} />
                  <Row name="Upstash Redis" status={sys.redis} />
                  <Row name="Telegram Bot"  status={sys.telegram} />
                  <Row name="Scheduler"     status={sys.scheduler}
                       extra={sys.job_count ? `${sys.job_count} jobs` : null} />
                </>
            }
          </div>

          {sys && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="TrueData" />
              <Row name="REST Auth Token"  status={sys.truedata_token} />
              <Row name="WebSocket Feed"   status={sys.truedata_ws} customOk="Connected" />
              <Row name="VIX"              status={sys.vix?.status}
                   extra={sys.vix?.value ? `${sys.vix.value}` : null} />
              <Row name="Market"           status={sys.market_open ? "ok" : "not_configured"}
                   customOk="Open" extra={sys.market_open ? null : "Closed"} />
            </div>
          )}

          {sys?.eod && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="EOD Bhavcopy (NSE)" />
              <Row name="NIFTY"     status={sys.eod.NIFTY} />
              <Row name="BANKNIFTY" status={sys.eod.BANKNIFTY} />
            </div>
          )}

          {sys?.pcr && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="PCR (Put-Call Ratio)" />
              <Row name="NIFTY"     status={sys.pcr.NIFTY} />
              <Row name="BANKNIFTY" status={sys.pcr.BANKNIFTY} />
            </div>
          )}

          {sys && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="Institutional &amp; Global Data" />
              <Row name="FII / DII (NSE)"    status={sys.inst_data} />
              <Row name="Gift Nifty (SGX)"   status={sys.global?.gift_nifty} />
              <Row name="DXY (Dollar Index)" status={sys.global?.dxy} />
              <Row name="Dow Jones"          status={sys.global?.dow} />
            </div>
          )}

          {sys && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="News Monitor" />
              <Row name="Last RSS Cycle"
                   status={sys.last_news_fetch ? "ok" : "missing"}
                   extra={sys.last_news_fetch || null} />
            </div>
          )}

          {sys?.ram?.pct != null && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-gray-400 text-xs uppercase tracking-wider">Server RAM</p>
                <span className={`text-xs font-mono ${ramText}`}>
                  {sys.ram.used_mb} / {sys.ram.total_mb} MB ({ramPct}%)
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-2 rounded-full transition-all ${ramColor}`} style={{ width: `${ramPct}%` }} />
              </div>
            </div>
          )}

          {sys && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="Signals Today" />
              <div className="grid grid-cols-5 gap-1 text-center">
                {[
                  { label: "Total",   n: sys.signals_today,                                              color: "text-gray-800" },
                  { label: "Winners", n: (sys.outcomes?.WINNER || 0) + (sys.outcomes?.BIG_WINNER || 0),  color: "text-green-600" },
                  { label: "Losers",  n: sys.outcomes?.LOSER || 0,                                       color: "text-red-600" },
                  { label: "Pending", n: sys.open_positions || 0,                                        color: "text-yellow-600" },
                  { label: "Expired", n: sys.outcomes?.EXPIRED || 0,                                     color: "text-gray-400" },
                ].map(({ label, n, color }) => (
                  <div key={label} className="bg-gray-50 rounded-lg py-2 px-1">
                    <p className={`text-lg font-bold font-mono leading-none ${color}`}>{n ?? 0}</p>
                    <p className="text-gray-400 text-xs mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SCHEDULER TAB ──────────────────────────────────── */}
      {activeTab === "scheduler" && (
        <div className="space-y-4">
          {sys?.jobs?.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <SectionHead title="Scheduled Jobs" />
              {sys.jobs.map(j => (
                <div key={j.id} className="flex justify-between text-xs py-2 border-b border-gray-100 last:border-0">
                  <span className="text-gray-700">{j.name}</span>
                  <span className="text-gray-400 font-mono">
                    {j.next_run
                      ? new Date(j.next_run).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) + " IST"
                      : "—"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-xs">
              {loading ? "Loading…" : "No job data — backend may be offline"}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Job Descriptions" />
            {[
              ["signal_scan",       "Every minute (9:15–15:30 IST) — scan all watchlist instruments, score signals"],
              ["vix_fetch",         "Every 5 min — fetch India VIX from TrueData"],
              ["eod_bhavcopy",      "15:35 IST — download NSE EOD bhavcopy for prev-close prices"],
              ["pcr_fetch",         "Every 15 min — fetch Put-Call Ratio from NSE"],
              ["inst_data_fetch",   "9:00 IST — fetch FII/DII institutional flow data"],
              ["global_data_fetch", "8:45 IST — fetch Gift Nifty, DXY, Dow from TrueData"],
              ["news_fetch",        "Every 10 min — scan RSS feeds for market-moving news"],
              ["outcome_tracker",   "Every 30 s — check open positions, mark WINNER/LOSER/EXPIRED"],
            ].map(([name, desc]) => (
              <div key={name} className="py-2 border-b border-gray-100 last:border-0">
                <p className="text-gray-800 font-mono text-xs">{name}</p>
                <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SCORE SYSTEM TAB ───────────────────────────────── */}
      {activeTab === "score" && (
        <div className="space-y-4">

          {/* Index thresholds */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="INDEX Signals (NIFTY / BANKNIFTY)" />
            <p className="text-gray-400 text-xs mb-3">Max score: 12 points</p>
            <div className="space-y-2">
              {[
                { range: "< 6.5",       action: "Skip — not stored",                              cls: "bg-gray-50 text-gray-500 border border-gray-200" },
                { range: "6.5 – 7.49",  action: "Analysis Log only — raw score saved, no Claude", cls: "bg-yellow-50 text-yellow-700 border border-yellow-200" },
                { range: "≥ 7.5",       action: "Claude analysis → Telegram alert + DB",          cls: "bg-green-50 text-green-700 border border-green-200" },
              ].map(r => (
                <div key={r.range} className={`flex justify-between items-center rounded-lg px-3 py-2 text-xs ${r.cls}`}>
                  <span className="font-mono font-semibold">{r.range}</span>
                  <span>{r.action}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stock thresholds */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="STOCK Signals" />
            <p className="text-gray-400 text-xs mb-3">Max score: 12 points</p>
            <div className="space-y-2">
              {[
                { range: "< 8.0",        action: "Skip — not stored",                               cls: "bg-gray-50 text-gray-500 border border-gray-200" },
                { range: "< 8.0",        action: "Skip — not stored",                               cls: "bg-gray-50 text-gray-500 border border-gray-200" },
                { range: "8.0 – 8.99",   action: "Analysis Log only — raw score saved, no Claude",  cls: "bg-yellow-50 text-yellow-700 border border-yellow-200" },
                { range: "9.0 – 10.49",  action: "Claude analysis → Telegram (WATCH/GOOD)",          cls: "bg-green-50 text-green-700 border border-green-200" },
                { range: "10.5 – 10.99", action: "Claude analysis → Telegram (STRONG)",              cls: "bg-blue-50 text-blue-700 border border-blue-200" },
                { range: "≥ 11.0",       action: "Claude analysis → Telegram (URGENT)",              cls: "bg-red-50 text-red-700 border border-red-200" },
              ].map(r => (
                <div key={r.range} className={`flex justify-between items-center rounded-lg px-3 py-2 text-xs ${r.cls}`}>
                  <span className="font-mono font-semibold">{r.range}</span>
                  <span>{r.action}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Low-liquidity window */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Low-Liquidity Window (11:00–13:30 IST)" />
            <p className="text-gray-400 text-xs mb-3">
              The old +0.25 threshold raise was removed — the flat-stock gate (range_util / oi_velocity / volume_ratio)
              is a more precise filter. The window is passed to Claude as <code className="bg-gray-100 px-1 rounded">in_low_liquidity_window=true</code> so Claude self-filters low-conviction setups.
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs">
                <span className="text-blue-700 font-semibold">11:00 – 13:30 IST</span>
                <span className="text-blue-600">context flag only — no score penalty</span>
              </div>
              <div className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs">
                <span className="text-gray-500 font-semibold">Flat-stock gate</span>
                <span className="text-gray-400">range_util / oi_velocity / volume_ratio — FADING blocks</span>
              </div>
              <div className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs">
                <span className="text-gray-500 font-semibold">FLAT_RANGE market state</span>
                <span className="text-gray-400">session range &lt;50% ATR + OI STEADY/FADING after 10:00 → block</span>
              </div>
            </div>
          </div>

          {/* Dynamic threshold */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Dynamic Threshold Adjustment" />
            <p className="text-gray-400 text-xs mb-3">
              Alert threshold shifts by ±0.5 based on NIFTY intraday move vs prev close
            </p>
            <div className="space-y-2">
              {[
                { cond: "NIFTY > +0.75% (Tailwind)",  adj: "−0.5 threshold", cls: "bg-green-50 border border-green-200 text-green-700" },
                { cond: "NIFTY < −0.75% (Headwind)",  adj: "+0.5 threshold", cls: "bg-red-50 border border-red-200 text-red-700" },
                { cond: "Within ±0.75% (Flat)",        adj: "No adjustment",  cls: "bg-gray-50 border border-gray-200 text-gray-500" },
              ].map(r => (
                <div key={r.cond} className={`flex justify-between items-center rounded-lg px-3 py-2 text-xs ${r.cls}`}>
                  <span>{r.cond}</span>
                  <span className="font-mono font-semibold">{r.adj}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Priority tiers */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Alert Priority Tiers" />
            <div className="space-y-1 mb-4">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">INDEX</p>
              {[
                { range: "≥ 11.0", label: "URGENT",  size: "2.0×", cls: "bg-red-50 text-red-700 border-red-200" },
                { range: "≥ 10.0", label: "STRONG",  size: "1.5×", cls: "bg-blue-50 text-blue-700 border-blue-200" },
                { range: "≥  9.0", label: "GOOD",    size: "1.0×", cls: "bg-green-50 text-green-700 border-green-200" },
                { range: "≥  7.5", label: "WATCH",   size: "0.5×", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
                { range: "<  7.5", label: "SKIP",    size: "—",    cls: "bg-gray-50 text-gray-500 border-gray-200" },
              ].map(r => (
                <div key={r.label} className={`flex justify-between items-center rounded-lg px-3 py-1.5 text-xs border ${r.cls}`}>
                  <span className="font-mono">{r.range}</span>
                  <span className="font-semibold">{r.label}</span>
                  <span className="font-mono">{r.size} size</span>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">STOCK</p>
              {[
                { range: "≥ 11.0", label: "URGENT", size: "2.0×", cls: "bg-red-50 text-red-700 border-red-200" },
                { range: "≥ 10.5", label: "STRONG", size: "1.5×", cls: "bg-blue-50 text-blue-700 border-blue-200" },
                { range: "≥  9.0", label: "GOOD",   size: "1.0×", cls: "bg-green-50 text-green-700 border-green-200" },
                { range: "≥  8.0", label: "WATCH",  size: "0.5×", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
                { range: "<  8.0", label: "SKIP",   size: "—",    cls: "bg-gray-50 text-gray-500 border-gray-200" },
              ].map(r => (
                <div key={r.label} className={`flex justify-between items-center rounded-lg px-3 py-1.5 text-xs border ${r.cls}`}>
                  <span className="font-mono">{r.range}</span>
                  <span className="font-semibold">{r.label}</span>
                  <span className="font-mono">{r.size} size</span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-2">Pipeline gate is 9.0 base for STOCK (adjusted ±0.5 by market direction). SR_TOUCH triggers use a separate lowered threshold.</p>
          </div>

          {/* Scoring components */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Score Components — INDEX v3.2 (12 pts)" />
            <p className="text-gray-400 text-xs mb-2">Tier 1: Market Edge (7 pts) · Tier 2: Trade Quality (3 pts) · Tier 3: Confirmation (2 pts)</p>
            <div className="space-y-1">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-1 mb-1">Tier 1 — Market Edge</p>
              {[
                ["OI Classification",       "2.5", "STRONG_LONG/SHORT_BUILDUP=2.5, MODERATE=1.5, UNWINDING=0"],
                ["Regime Alignment",        "2.5", "Strategy matches regime=2.5, partial=1.25, opposes=0"],
                ["Breadth Alignment",       "2.0", "STRONG_BULL=2.0, BULL=1.5, NEUTRAL=0.75, BEAR=0"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-3 mb-1">Tier 2 — Trade Quality</p>
              {[
                ["VWAP Structure",          "1.0", "VWAP alignment & momentum"],
                ["Multi-Timeframe Confirm", "1.0", "1m/5m/15m all aligned"],
                ["Volume Expansion",        "0.5", ">150%=0.5, 120–150%=0.25, normal=0"],
                ["Trade Structure Quality", "0.5", "HIGH=0.5, MEDIUM=0.35, LOW=0.15, none=0.05"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-3 mb-1">Tier 3 — Confirmation</p>
              {[
                ["Option Structure Quality","0.5", "IV, OI walls, liquidity, max pain"],
                ["FII Positioning",         "0.5", "FII futures L/S + options net bias"],
                ["PCR Reading",             "0.5", "Put/call ratio alignment"],
                ["RSI/MACD Confirmation",   "0.5", "RSI zone + MACD trend alignment"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-2">Deprecated (0.0): VIX, Pro vs Retail, Global Context, Trend Quality, Entry Timing, Chart Pattern, Candlestick at S/R, S/R Alignment, IV Rank, Max Pain, OI Buildup</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Score Components — STOCK v3.2 (12 pts)" />
            <p className="text-gray-400 text-xs mb-2">Tier 1: Market Edge (7 pts) · Tier 2: Trade Quality (3 pts) · Tier 3: Confirmation (2 pts)</p>
            <div className="space-y-1">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-1 mb-1">Tier 1 — Market Edge</p>
              {[
                ["Relative Strength",       "2.5", "TOP_10=2.5, TOP_25=2.0, TOP_50=1.25, BOTTOM_50=0"],
                ["OI Classification",       "2.5", "STRONG_BUILDUP=2.5, MODERATE=1.5, weak=0"],
                ["Sector Momentum",         "2.0", "STRONG=2.0, MODERATE=1.0, WEAK=0"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-3 mb-1">Tier 2 — Trade Quality</p>
              {[
                ["VWAP Structure",          "1.0", "VWAP alignment & momentum"],
                ["Multi-Timeframe Confirm", "1.0", "1m/5m/15m all aligned"],
                ["Volume Expansion",        "1.0", ">200%=1.0, 150–200%=0.5, normal=0"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mt-3 mb-1">Tier 3 — Confirmation</p>
              {[
                ["Option Structure Quality","0.5", "IV, OI walls, liquidity"],
                ["PCR Reading",             "0.5", "Put/call ratio alignment"],
                ["RSI/MACD Confirmation",   "0.5", "RSI zone + MACD trend alignment"],
                ["Trade Structure Quality", "0.5", "HIGH=0.5, MEDIUM=0.35, LOW=0.15, none=0.05"],
              ].map(([comp, pts, note]) => (
                <div key={comp} className="flex items-start gap-2 text-xs py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-800 font-mono font-semibold w-8 flex-shrink-0">{pts}</span>
                  <span className="text-gray-700 font-medium w-40 flex-shrink-0">{comp}</span>
                  <span className="text-gray-400 italic">{note}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-2">Hard gate: Option Liquidity (0 OI = SKIP regardless of score). Deprecated (0.0): Delivery%, Trend Quality, Entry Timing, Chart Pattern, S/R Alignment, Candlestick Trigger, IV Rank, Max Pain, OI Buildup</p>
          </div>
        </div>
      )}

      {/* ── CONFIG TAB ─────────────────────────────────────── */}
      {activeTab === "config" && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="System Info" />
            <InfoRow name="Version"  value={root?.version} />
            <InfoRow name="API Base" value={API} mono />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <SectionHead title="Configuration" />
            <p className="text-gray-500 text-xs leading-relaxed">
              All settings are in <code className="bg-gray-100 px-1 rounded">backend/.env</code>.
              Restart the backend after any changes. Never commit <code className="bg-gray-100 px-1 rounded">.env</code> to Git.
            </p>
            <p className="text-gray-500 text-xs leading-relaxed">
              TradingView webhook: <code className="bg-gray-100 px-1 rounded font-mono break-all">{API}/webhook/tradingview</code>
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <SectionHead title="Threshold Config Location" />
            <p className="text-gray-500 text-xs mb-3">Edit in <code className="bg-gray-100 px-1 rounded">backend/scheduler/jobs.py</code> — top of file constants</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs space-y-1">
              <p><span className="text-purple-600">_INDEX_CLAUDE_THRESHOLD</span> <span className="text-gray-400">=</span> <span className="text-green-600">7.5</span>  <span className="text-gray-400"># INDEX: Claude + Telegram threshold</span></p>
              <p><span className="text-purple-600">_INDEX_ALERT_THRESHOLD</span>  <span className="text-gray-400">=</span> <span className="text-green-600">7.5</span>  <span className="text-gray-400"># adjusted ±0.5 by market direction</span></p>
              <p><span className="text-purple-600">_STOCK_CLAUDE_THRESHOLD</span> <span className="text-gray-400">=</span> <span className="text-green-600">9.0</span>  <span className="text-gray-400"># STOCK: Claude + Telegram threshold</span></p>
              <p><span className="text-purple-600">_STOCK_ALERT_THRESHOLD</span>  <span className="text-gray-400">=</span> <span className="text-green-600">9.0</span>  <span className="text-gray-400"># adjusted ±0.5 by market direction</span></p>
              <p><span className="text-purple-600">_COOLDOWN_SECONDS</span>        <span className="text-gray-400">=</span> <span className="text-blue-600">2700</span> <span className="text-gray-400"># 45 min per-symbol per-direction</span></p>
              <p><span className="text-purple-600">_ANALYSIS_COOLDOWN_SECONDS</span> <span className="text-gray-400">=</span> <span className="text-blue-600">300</span>  <span className="text-gray-400"># 5 min between raw-score DB logs</span></p>
              <p><span className="text-purple-600">_AVOID_START</span>             <span className="text-gray-400">=</span> <span className="text-yellow-600">11:00</span> <span className="text-gray-400"># IST — low-liquidity window start</span></p>
              <p><span className="text-purple-600">_AVOID_END</span>               <span className="text-gray-400">=</span> <span className="text-yellow-600">13:30</span> <span className="text-gray-400"># IST — context flag only, no threshold raise</span></p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
